/**
 * Stable error codes and their HTTP status mapping.
 *
 * FRS §8.6.3: `code` is machine-readable, never localised and never renamed.
 * `message` is localised to the caller's language and is for humans only — no
 * client may branch on it.
 *
 * FRS §8.6.4 fixes which status each class of failure returns. Keeping the
 * mapping in one table is what makes that guarantee mechanical rather than a
 * per-handler judgement call. In particular:
 *
 *   403 = you are authenticated but lack the capability
 *   404 = the record exists but is outside your company scope, OR does not exist
 *
 * The two are indistinguishable *by design* (BR-006). That is a security
 * boundary, not a style preference: a 403 on an out-of-scope record would
 * confirm that record's existence.
 */

export const ERROR_STATUS = {
  // 400 — the server could not parse the request at all.
  malformed_request: 400,

  // 401 — missing, expired or invalid credentials. FEAT-001 requires a single
  // generic failure for bad credentials so accounts cannot be enumerated.
  authentication_required: 401,
  invalid_credentials: 401,
  token_invalid: 401,
  token_expired: 401,

  // 403 — authenticated, but not allowed.
  capability_denied: 403,
  segregation_of_duties: 403, // REQ-013: cannot action a record you raised
  immutable_record: 403, // system chief; audit log writes
  audit_log_is_append_only: 403, // FEAT-013: rejected for every role
  /*
   * FEAT-006 — the caller may not book this room.
   *
   * Distinct from `capability_denied` because room access is not a capability:
   * it comes from the room's allow-lists (BR-019), which an administrator edits
   * per room. Merging the two would make "your role cannot do this" and "this
   * particular room is not yours to book" the same message, and only one of
   * them is fixed by a role change.
   *
   * 403 rather than 404 is the FRS's own choice here, and it leaks nothing: the
   * room list the caller was offered already told them the room exists.
   */
  room_access_denied: 403,

  // 404 — not found, or outside the caller's scope. Deliberately ambiguous.
  not_found: 404,

  // 409 — conflicts with current state.
  duplicate_name: 409,
  duplicate_email: 409,
  booking_conflict: 409, // FEAT-006: body carries the conflicting slot
  already_checked_out: 409, // BR-022
  provider_not_configured: 409, // FEAT-022: names missing keys, never values
  state_conflict: 409,

  // 410 — single-use token already consumed or expired.
  token_consumed: 410,

  // 413 / 415 — uploads.
  payload_too_large: 413,
  unsupported_media_type: 415,

  // 422 — well-formed but semantically invalid.
  validation_failed: 422,
  illegal_transition: 422, // FEAT-005 ticket state machine
  unknown_field: 422, // FEAT-011: unknown keys are rejected, never ignored
  unknown_setting_key: 422, // FEAT-023 REQ-137
  role_not_assignable: 422, // FEAT-012 three-axis role constraint
  retired_role: 422, // ASM-07
  /*
   * FEAT-006 — BR-055. The room is under maintenance or closed, so it accepts
   * no new or relocated reservation. The message carries the room's status
   * label, as the FRS's validation table specifies.
   */
  room_unavailable: 422,
  /*
   * FEAT-006 — BR-017. "You cannot book a time in the past."
   *
   * Its own code rather than a `validation_failed` field error because the
   * client reacts to it differently: the offending value is legal input that has
   * merely gone stale while the form was open, and the right response is to
   * re-offer the nearest slot rather than to mark the field wrong.
   */
  booking_in_past: 422,

  // 423 / 429 — account and rate limiting.
  account_locked: 423,
  rate_limited: 429,

  // 500 — internals are never disclosed; the correlation id is.
  internal_error: 500,
} as const satisfies Record<string, number>;

export type ErrorCode = keyof typeof ERROR_STATUS;

export const ERROR_CODES = Object.keys(ERROR_STATUS) as ErrorCode[];

export function statusForErrorCode(code: ErrorCode): number {
  return ERROR_STATUS[code];
}

export function isErrorCode(value: string): value is ErrorCode {
  return Object.prototype.hasOwnProperty.call(ERROR_STATUS, value);
}
